import { useFormatDate } from "#hooks/useFormatDate.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type {
    ConversationSource,
    GmailMessageCustomProperties,
    GoogleCalendarEventCustomProperties,
    JiraIssueCustomProperties,
    OutlookCalendarEventCustomProperties,
    OutlookMessageCustomProperties,
    SharepointFileCustomProperties,
    SharepointPageCustomProperties,
    SlackMessageCustomProperties,
    TeamsMessageCustomProperties,
    WorkleapFileLibraryFileCustomProperties,
    WorkleapOfficevibeFeedbackCustomProperties,
    WorkleapOfficevibeOneOnOneAgendaCustomProperties,
    WorkleapOfficevibeRecognitionCustomProperties
} from "#types/types.ts";
import { useMemo } from "react";

interface SourcePreviewModel {
    title: string;
    author: string | null;
    date: string | null;
    description: string;
}

export const useConversationSource = (source: ConversationSource) => {
    const { tShared } = useSharedTranslation();
    const formatDate = useFormatDate();

    return useMemo<SourcePreviewModel>(() => {
        const {
            title,
            createdBy,
            updatedBy,
            createdAt,
            updatedAt,
            sourceType
        } = source;

        // The schema types every source field as present, but a source is third-party data relayed
        // by the API: a connector that returns no body leaves these empty. Every branch below feeds
        // `preview` to a renderer that assumes a string and destructures `customProperties`, so
        // normalize both once here rather than in each branch.
        const preview = source.preview ?? "";
        const customProperties = source.customProperties ?? {} as ConversationSource["customProperties"];
        const creator = createdBy?.fullName ?? null;
        const updaterOrCreator = updatedBy?.fullName ?? creator;

        // Only label a date that formatted. `formatDate` returns null for a date the API sent in a
        // shape `Date` cannot parse, and a bare "Updated " with nothing after it reads as a bug.
        const labelDate = (
            key: "updatedAt" | "weekOf",
            dateString: string | null | undefined,
            options?: Parameters<typeof formatDate>[1]
        ) => {
            const date = formatDate(dateString, options);

            return date ? tShared(key, { date }) : null;
        };

        switch (sourceType) {
            case "slack_message": {
                const { fromName } =
                    customProperties as SlackMessageCustomProperties;

                return {
                    title,
                    author: fromName ?? creator,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
            }

            case "outlook_message": {
                const { fromName } =
                    customProperties as OutlookMessageCustomProperties;

                return {
                    title,
                    author: fromName ?? creator,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
            }

            case "outlook_calendar_event": {
                const { organizerEmail, start, isAllDay } =
                    customProperties as OutlookCalendarEventCustomProperties;

                return {
                    title,
                    author: organizerEmail ?? creator,
                    date: formatDate(start, { hideTime: isAllDay }),
                    description: preview
                };
            }

            case "teams_message": {
                const { fromName } =
                    customProperties as TeamsMessageCustomProperties;

                return {
                    title,
                    author: fromName ?? creator,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
            }

            case "confluence_page":
                return {
                    title,
                    author: updaterOrCreator,
                    date: labelDate("updatedAt", updatedAt, { hideTime: true }),
                    description: preview
                };

            case "workleap_officevibe_feedback": {
                const { isAnonymous } =
                    customProperties as WorkleapOfficevibeFeedbackCustomProperties;

                const _date = isAnonymous
                    ? labelDate("weekOf", createdAt, { hideTime: true, forceUTC: true })
                    : formatDate(createdAt, { hideTime: true });

                return {
                    title,
                    author: creator,
                    date: _date,
                    description: preview
                };
            }

            case "workleap_files_library_file": {
                const { fileName } =
                    customProperties as WorkleapFileLibraryFileCustomProperties;

                return {
                    title: fileName,
                    author: creator ?? "Workleap",
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
            }

            case "workleap_officevibe_engagement_score": {
                return {
                    title,
                    author: null,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
            }

            case "workleap_officevibe_oneonone_agenda": {
                const { teamMember } =
                    customProperties as WorkleapOfficevibeOneOnOneAgendaCustomProperties;

                const otherParticipantName = teamMember?.fullName;

                return {
                    title,
                    author: otherParticipantName ?? null,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
            }

            case "workleap_officevibe_recognition": {
                const { recognitionMessage, recipients: _recipients } =
                    customProperties as WorkleapOfficevibeRecognitionCustomProperties;

                const recipients = _recipients ?? [];

                const _title = tShared("recognitionRecipients", {
                    count: recipients.length,
                    recipient: recipients[0]?.fullName
                });

                const creatorToRecipients = creator
                    ? tShared("recognitionCreatorToRecipients", {
                        count: recipients.length,
                        creator,
                        recipient: recipients[0]?.fullName
                    })
                    : null;

                return {
                    title: _title,
                    author: creatorToRecipients,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: recognitionMessage ?? ""
                };
            }

            case "sharepoint_file": {
                const { fileName, authorName } =
                    customProperties as SharepointFileCustomProperties;

                return {
                    title: fileName,
                    author: authorName ?? updaterOrCreator,
                    date: labelDate("updatedAt", updatedAt, { hideTime: true }),
                    description: preview
                };
            }

            case "sharepoint_page": {
                const { authorName } =
                    customProperties as SharepointPageCustomProperties;

                return {
                    title,
                    author: authorName ?? updaterOrCreator,
                    date: labelDate("updatedAt", updatedAt, { hideTime: true }),
                    description: preview
                };
            }

            case "web_page":
                return {
                    title,
                    author: null,
                    date: null,
                    description: preview
                };
            case "jira_issue": {
                const { assigneeDisplayName } =
                    customProperties as JiraIssueCustomProperties;

                const cleanPreview = preview.replace(/<[^>]*>/g, "").trim();

                return {
                    title,
                    author: assigneeDisplayName ?? tShared("jiraUnassigned"),
                    date: formatDate(createdAt, { hideTime: true }),
                    description: cleanPreview
                };
            }
            case "notion_page": {
                return {
                    title,
                    author: null,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
            }
            case "google_drive_file":
                return {
                    title,
                    author: creator,
                    date: formatDate(updatedAt, { hideTime: true }),
                    description: preview
                };
            case "google_calendar_event": {
                const { organizerEmail, start, isAllDay } =
                    customProperties as GoogleCalendarEventCustomProperties;

                return {
                    title,
                    author: organizerEmail ?? creator,
                    date: formatDate(start, { hideTime: isAllDay }),
                    description: preview
                };
            }
            case "gmail_message": {
                const { sender, sentDate } =
                    customProperties as GmailMessageCustomProperties;

                return {
                    title,
                    author: sender ?? creator,
                    date: formatDate(sentDate ?? createdAt, { hideTime: true }),
                    description: preview
                };
            }
            case "table":
                return {
                    title,
                    author: null,
                    date: null,
                    description: preview
                };
            default:
                return {
                    title,
                    author: creator,
                    date: formatDate(createdAt, { hideTime: true }),
                    description: preview
                };
        }
    }, [formatDate, source, tShared]);
};
